import { Step as ModelStep } from "@redotech/redo-model/return-flow";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import TagIcon from "@redotech/redo-web/icon-old/tag.svg";
import { produce } from "immer";
import * as React from "react";
import {
  StepDownstream,
  StepId,
  StepType,
  StepTypeDetailsProps,
} from "../step";

export interface DiscountCodeNameState {
  prefix: string;
  suffix: string;
  next: StepId | undefined;
  customTitle?: string;
}

export const DISCOUNT_CODE_NAME = (
  Details: React.ComponentType<StepTypeDetailsProps<DiscountCodeNameState>>,
): StepType<DiscountCodeNameState, ModelStep.DiscountCodeName> => ({
  Details: (props: StepTypeDetailsProps<DiscountCodeNameState>) =>
    React.createElement(Details, props),
  customTitle(state) {
    return state.customTitle;
  },
  title: "Discount code name",
  valid(state) {
    return !!state.prefix || !!state.suffix;
  },
  Icon: TagIcon,
  description(state) {
    return `${state.prefix}...${state.suffix}`;
  },
  downstream(state) {
    const result: StepDownstream[] = [];
    if (state.next !== undefined) {
      result.push({ id: state.next });
    }
    return result;
  },
  maxDownstream() {
    return 1;
  },
  empty: { next: undefined, prefix: "", suffix: "" },
  stepDeleted(state, stepId) {
    return produce(state, (state) => {
      if (state.next === stepId) {
        state.next = undefined;
      }
    });
  },
  updateConnection(state, handleIndex, targetStepId) {
    if (handleIndex !== 0) {
      throw new Error(
        `Invalid handle index ${handleIndex} for Discount Code Name step`,
      );
    }
    return produce(state, (draft) => {
      draft.next = targetStepId;
    });
  },
  removeConnection(state, targetStepId) {
    return produce(state, (draft) => {
      if (draft.next === targetStepId) {
        draft.next = undefined;
      }
    });
  },
  layout() {
    return BlockLayout.FULL;
  },
  toModel(state, id) {
    return {
      customTitle: state.customTitle,
      type: ModelStep.DISCOUNT_CODE_NAME,
      next: state.next ? id(state.next) : undefined,
      prefix: state.prefix,
      suffix: state.suffix,
    };
  },
  fromModel(model, id) {
    return {
      customTitle: model.customTitle,
      next: model.next ? id(model.next) : undefined,
      prefix: model.prefix || "",
      suffix: model.suffix || "",
    };
  },
});
